import {
  ActionList,
  ActionListItem,
  ActionMenu,
  Alert,
  AlertActions,
  AlertContent,
  Button,
  Icon,
  Loader,
  Notice,
  Offscreen,
  Panel
} from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AdminAnnouncement,
  type AdminAnnouncementListItem,
  type AnnouncementSeverity,
  deleteAdminAnnouncement
} from '~/common/api-client';
import { useGlobalToast } from '~/common/contexts/globalToast';
import { useProducts } from '~/common/contexts/products';
import { dayjs } from '~/common/utils/dates';
import useAdminAnnouncements from '../../hooks/useAdminAnnouncements';
import styles from './Announcements.css';
import { PSEUDO_PRODUCT_NAMES } from './announcementsDisplay';

interface AnnouncementsListProps {
  token: string;
  onNew: () => void;
  onEdit: (announcement: AdminAnnouncement) => void;
  onDuplicate: (announcement: AdminAnnouncement) => void;
}

interface RowHandlers {
  onEdit: (a: AdminAnnouncement) => void;
  onDuplicate: (a: AdminAnnouncement) => void;
  onCopyId: (a: AdminAnnouncement) => void;
  onRequestDelete: (a: AdminAnnouncement) => void;
}

const MetaField = ({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <span>
    <span className={styles.metaLabel}>{label}: </span>
    <span className={styles.metaValue}>{value}</span>
  </span>
);

const AnnouncementCard = ({
  announcement,
  onEdit,
  onDuplicate,
  onCopyId,
  onRequestDelete
}: { announcement: AdminAnnouncementListItem } & RowHandlers) => {
  const { t } = useTranslation();
  const { getProductBySlug } = useProducts();

  const severityLabels: Record<AnnouncementSeverity, string> = {
    informational: t('Informational'),
    warning: t('Warning'),
    critical: t('Critical')
  };

  const productName =
    getProductBySlug(announcement.product_slug)?.name ??
    PSEUDO_PRODUCT_NAMES[announcement.product_slug] ??
    announcement.product_slug;

  // Every card renders the same visible button labels, so append the (offscreen)
  // announcement description to keep each accessible name unique.
  const cardContext = <Offscreen> — {announcement.description}</Offscreen>;

  return (
    <Panel padding className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{announcement.description}</h3>
        <div className={styles.cardActions}>
          <Button variant="secondary" thin onClick={() => onEdit(announcement)}>
            <Icon type="pencil" />
            {t('Edit announcement')}
            {cardContext}
          </Button>
          <ActionMenu
            trigger={
              <Button variant="secondary" thin>
                <Icon type="kabob" />
                {t('More actions')}
                {cardContext}
              </Button>
            }
          >
            <ActionList>
              <ActionListItem onAction={() => onDuplicate(announcement)}>
                {t('Duplicate announcement')}
              </ActionListItem>
              <ActionListItem onAction={() => onCopyId(announcement)}>
                {t('Copy announcement ID')}
              </ActionListItem>
              <ActionListItem
                variant="danger"
                leadingIcon="trash"
                onAction={() => onRequestDelete(announcement)}
              >
                {t('Delete announcement')}
              </ActionListItem>
            </ActionList>
          </ActionMenu>
        </div>
      </div>
      <div className={styles.cardMeta}>
        <MetaField label={t('Type')} value={t('Toast')} />
        <MetaField
          label={t('Severity')}
          value={severityLabels[announcement.severity]}
        />
        <MetaField
          label={t('Author')}
          value={announcement.created_by_name || t('Unknown')}
        />
        <MetaField
          label={t('Date')}
          value={t('{{start}} to {{end}}', {
            start: dayjs(announcement.starts_at).format('MMMM D, h:mmA'),
            end: dayjs(announcement.ends_at).format('MMMM D, h:mmA')
          })}
        />
        <MetaField label={t('Products')} value={productName} />
        {announcement.updated_by_name && (
          <MetaField
            label={t('Last edited')}
            value={announcement.updated_by_name}
          />
        )}
      </div>
    </Panel>
  );
};

const AnnouncementsSection = ({
  heading,
  announcements,
  ...rowHandlers
}: {
  heading: string;
  announcements: AdminAnnouncementListItem[];
} & RowHandlers) => {
  const { t } = useTranslation();
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>{heading}</h2>
      {announcements.length === 0 ? (
        <p className={styles.empty}>{t('Nothing here yet.')}</p>
      ) : (
        <ul className={styles.cardList}>
          {announcements.map(announcement => (
            <li key={announcement.id}>
              <AnnouncementCard announcement={announcement} {...rowHandlers} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const AnnouncementsList: React.FC<AnnouncementsListProps> = ({
  token,
  onNew,
  onEdit,
  onDuplicate
}) => {
  const { t } = useTranslation();
  const { setContents } = useGlobalToast();
  const { announcements, loading, error, refresh } =
    useAdminAnnouncements(token);
  const [pendingDelete, setPendingDelete] =
    React.useState<AdminAnnouncement | null>(null);

  const active = announcements.filter(a => a.status === 'active');
  const scheduled = announcements.filter(a => a.status === 'scheduled');

  const onCopyId = async (announcement: AdminAnnouncement) => {
    try {
      await navigator.clipboard.writeText(announcement.id);
      setContents(t('Announcement ID copied to clipboard'), 'confirmation');
    } catch {
      setContents(t('Could not copy the announcement ID'), 'error');
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      await deleteAdminAnnouncement({ token, id: target.id });
      setContents(t('Announcement deleted'), 'confirmation');
      refresh();
    } catch (err) {
      // The global toast announces the failure to screen reader users too, and
      // surfaces the API's own message (matching the create/edit flows).
      setContents(
        err instanceof Error && err.message
          ? err.message
          : t('Failed to delete the announcement. Please try again.'),
        'error'
      );
      // Re-fetch regardless: if the announcement was already deleted elsewhere
      // (404), the stale card disappears instead of lingering until a reload.
      refresh();
    }
  };

  const rowHandlers: RowHandlers = {
    onEdit,
    onDuplicate,
    onCopyId,
    onRequestDelete: setPendingDelete
  };

  return (
    <div className={styles.list}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('Customer announcements')}</h1>
        <Button
          variant="secondary"
          className={styles.newButton}
          onClick={onNew}
        >
          <Icon type="plus" />
          {t('New announcement')}
        </Button>
      </div>

      {error && (
        <Notice type="caution" title={t('Could not load announcements')}>
          {error.message}
        </Notice>
      )}

      {loading ? (
        <Loader label={t('Loading announcements')} />
      ) : (
        <>
          <div className={styles.stats}>
            <Panel padding className={styles.statCard}>
              <span className={styles.statLabel}>{t('Active')}</span>
              <span className={styles.statCount}>{active.length}</span>
            </Panel>
            <Panel padding className={styles.statCard}>
              <span className={styles.statLabel}>{t('Scheduled')}</span>
              <span className={styles.statCount}>{scheduled.length}</span>
            </Panel>
          </div>

          <AnnouncementsSection
            heading={t('Active')}
            announcements={active}
            {...rowHandlers}
          />
          <AnnouncementsSection
            heading={t('Scheduled ({{count}})', { count: scheduled.length })}
            announcements={scheduled}
            {...rowHandlers}
          />
        </>
      )}

      {pendingDelete && (
        <Alert
          show
          variant="warning"
          heading={<>{t('Delete announcement')}</>}
          onClose={() => setPendingDelete(null)}
        >
          <AlertContent>
            {t(
              'Are you sure you want to delete this announcement? This cannot be undone.'
            )}
          </AlertContent>
          <AlertActions>
            <Button variant="error" onClick={confirmDelete}>
              {t('Delete')}
            </Button>
            <Button variant="secondary" onClick={() => setPendingDelete(null)}>
              {t('Cancel')}
            </Button>
          </AlertActions>
        </Alert>
      )}
    </div>
  );
};

export default AnnouncementsList;
